import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Close } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import Button from "@mui/material/Button";

import { ErrorComponent } from "../../../../components/error/error.component";
import Loading from "../../../../components/loading/loading";
import ProgressIndicator from "../../../../components/progress/progress-indicator.component";
import { useAnalysisProgress } from "../../../../hooks/useAnalysisProgress";
import { Analysis, AnalysisStatus } from "../../../../models/general/analysis";
import { URLS } from "../../../../services/app-urls";
import { BaseService } from "../../../../services/base-service";
import { translate } from "../../../../translate/translate";
import { orchestrator } from "../../../../websocket/orchestrator";

const STATUS_POLLING_INTERVAL_MS = 10000;
const FINAL_STATUSES = [AnalysisStatus.COMPLETED, AnalysisStatus.ERROR, AnalysisStatus.CANCELED];

const AnalysisProcessingPageComponent = () => {
  const navigate = useNavigate();
  const { action: analysisId } = useParams();
  const analysisInfo = useAnalysisProgress(Number(analysisId));

  const service = new BaseService<Analysis>(URLS.ANALYSIS);

  const [forecastName, setForecastName] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  const status = analysisInfo?.status ?? 'WAITING';

  const percentage = analysisInfo?.progress ?? 0;

  const remaining = analysisInfo?.remaining;

  // Reprocess (simulation), new tab and F5 reach this page without opening the socket
  // first; addConnection is a no-op when it already exists.
  useEffect(() => {
    if (!analysisId) return;
    orchestrator.addConnection({
      route: 'analysis',
      id: Number(analysisId),
      storageKey: `analysis_${analysisId}`,
    });
  }, [analysisId]);

  // Safety net for when the socket is down: poll the REST status until it is final.
  useEffect(() => {
    if (!analysisId) return;

    const timer = setInterval(async () => {
      try {
        const response = await service.getById(analysisId);
        const statusPing = response?.data?.status;

        if (statusPing && FINAL_STATUSES.includes(statusPing)) {
          clearInterval(timer);
          navigateByEnumStatus(statusPing);
        }
      } catch (error) {
        // transient failure: keep polling
      }
    }, STATUS_POLLING_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [analysisId]);

  const titleText = useMemo(() => {
    switch (status) {
      case 'WAITING':
        return translate.t("analysis_waiting_start");
      case 'PROCESSING':
        return translate.t("processing_analysis") + " (" + percentage + "%)";
      default:
        return translate.t("processing_analysis") + " (" + percentage + "%)";
    }
  }, [status, percentage]);

  const informationText = useMemo(() => {
    switch (status) {
      case 'WAITING':
        return translate.t("analysis_waiting_in_queue");
      case 'PROCESSING':
        return remaining
          ? translate.t("time_remaining") + " " + remaining
          : translate.t("calc_available_demand");
      default:
        return translate.t("calc_available_demand");
    }
  }, [status, remaining]);

  const getForecastName = () => {
    if (!analysisId) return;

    service.clearParameters();
    service
      .getById(analysisId)
      .then(({ data }) => {
        setForecastName(data?.description);
      })
      .catch(error => ErrorComponent(error));
  };

  const redirect = () => {
    navigate("/general/analysis", { replace: true });
  };

  const cancelAnalysis = () => {
    if (!analysisId) return;

    setLoading(true);
    service.clearParameters();
    service
      .update({ status: AnalysisStatus.CANCELED }, Number(analysisId))
      .then(() => {
        redirect();
      })
      .catch(error => ErrorComponent(error))
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getForecastName();
  }, []);

  useEffect(() => {
    navigateByStorageStatus(status);
  }, [status, navigate, analysisId]);

  const navigateByStorageStatus = (s: string) => {
    switch (s) {
      case 'FINISHED':
      case 'COMPLETED':
        return navigate(`/general/analysis/${analysisId}/gds-calculation`, { replace: true });
      case 'ERROR':
        return navigate(`/general/analysis/${analysisId}/error`, { replace: true });
      case 'CANCELED':
        return navigate("/general/analysis", { replace: true });
      default:
        break;
    }
  };

  const navigateByEnumStatus = (s: AnalysisStatus) => {
    switch (s) {
      case AnalysisStatus.COMPLETED:
        return navigate(`/general/analysis/${analysisId}/gds-calculation`, { replace: true });
      case AnalysisStatus.ERROR:
        return navigate(`/general/analysis/${analysisId}/error`, { replace: true });
      case AnalysisStatus.CANCELED:
        return navigate("/general/analysis", { replace: true });
      default:
        break;
    }
  };

  const isActive = status === 'WAITING' || status === 'PROCESSING';

  return (
    <>
      <Loading open={loading} />
      <section>
        <div
          className="floating-title floating-title-item"
          style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <IconButton onClick={redirect} id="back-analysis-page">
            <ArrowBackIcon color="primary" fontSize="large" fontWeight="fontWeightBold" />
          </IconButton>
          <Typography variant={"h5"} id={forecastName}>
            {forecastName}
          </Typography>
        </div>
        <Paper>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: 10,
            }}
          >
            <ProgressIndicator
              value={percentage}
              title={titleText}
              subtitle={informationText}
              additionalText={translate.t("process_information")}
            >
              {isActive && (
                <Button
                  startIcon={<Close />}
                  color="error"
                  variant="contained"
                  sx={{ marginTop: "20px" }}
                  onClick={cancelAnalysis}
                >
                  {translate.t("cancel_processing")}
                </Button>
              )}
            </ProgressIndicator>
          </Box>
        </Paper>
      </section>
    </>
  );
};

export default AnalysisProcessingPageComponent;
